import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supportAPI } from '@services/support.service';

// ---------------------------------------------------------------------------
// Async Thunks
// ---------------------------------------------------------------------------

export const fetchConversations = createAsyncThunk(
  'support/fetchConversations',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await supportAPI.getConversations(params);
    } catch (err) {
      return rejectWithValue(err.userMessage || 'Failed to load conversations');
    }
  }
);

export const fetchConversationDetail = createAsyncThunk(
  'support/fetchConversationDetail',
  async (id, { rejectWithValue }) => {
    try {
      return await supportAPI.getConversationDetail(id);
    } catch (err) {
      return rejectWithValue(err.userMessage || 'Failed to load conversation detail');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'support/fetchMessages',
  async ({ conversationId, page = 1 }, { rejectWithValue }) => {
    try {
      const data = await supportAPI.getMessages(conversationId, page);
      return { conversationId, page, ...data };
    } catch (err) {
      return rejectWithValue(err.userMessage || 'Failed to load messages');
    }
  }
);

export const fetchStats = createAsyncThunk(
  'support/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      return await supportAPI.getStats();
    } catch (err) {
      return rejectWithValue(err.userMessage || 'Failed to load stats');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'support/sendMessage',
  async (payload, { rejectWithValue }) => {
    try {
      return await supportAPI.sendMessage(payload);
    } catch (err) {
      return rejectWithValue(err.userMessage || 'Failed to send message');
    }
  }
);

export const resolveConversation = createAsyncThunk(
  'support/resolveConversation',
  async ({ id }, { rejectWithValue }) => {
    try {
      await supportAPI.closeConversation(id);
      return { id, status: 'CLOSED' };
    } catch (err) {
      return rejectWithValue(err.userMessage || 'Failed to close conversation');
    }
  }
);

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState = {
  // Conversation list
  conversations: [],
  conversationsTotal: 0,
  conversationsPage: 1,
  conversationsHasMore: true,

  // Filters
  filters: {
    status: '',
    search: '',
  },

  // Currently selected conversation
  selectedId: null,
  detail: null,
  // Incremented when the currently-open conversation is reopened via WS,
  // so ChatWindow can re-fetch messages + detail automatically.
  conversationRefreshToken: 0,

  // Messages keyed by conversationId
  // { [convId]: { data: [], page: 1, hasMore: true } }
  messages: {},

  // Stats bar
  stats: null,

  // WebSocket
  wsConnected: false,

  // Typing indicators: { [convId]: boolean }
  typingMap: {},

  // Total unread for sidebar badge
  unreadTotal: 0,

  // Loading states
  loading: {
    conversations: false,
    detail: false,
    messages: false,
    stats: false,
    sending: false,
    resolving: false,
  },

  error: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const calcUnreadTotal = (conversations) =>
  conversations.reduce((sum, c) => sum + (c.agent_unread_count || 0), 0);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    setSelectedId(state, action) {
      state.selectedId = action.payload;
      // Reset detail when switching conversations
      state.detail = null;
    },

    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    wsConnected(state) {
      state.wsConnected = true;
    },

    wsDisconnected(state) {
      state.wsConnected = false;
    },

    // WS: new conversation broadcast (also fires when customer reopens a closed one)
    receiveNewConversation(state, action) {
      const conv = action.payload;
      const idx = state.conversations.findIndex((c) => c.id === conv.id);
      if (idx !== -1) {
        // Existing conversation — update fields and move to top
        state.conversations[idx] = { ...state.conversations[idx], ...conv };
        const [updated] = state.conversations.splice(idx, 1);
        state.conversations.unshift(updated);

        // If this is the conversation the agent currently has open, trigger a full refresh
        if (state.selectedId === conv.id) {
          state.detail = null; // clear stale detail so ChatWindow shows loading briefly
          state.conversationRefreshToken += 1;
        }
      } else {
        state.conversations.unshift(conv);
      }
      state.unreadTotal = calcUnreadTotal(state.conversations);
    },

    // WS: new message received
    receiveNewMessage(state, action) {
      const msg = action.payload;
      const convId = msg.conversation_id;

      // Append to messages if we have that conversation loaded
      if (state.messages[convId]) {
        const alreadyExists = state.messages[convId].data.find((m) => m.id === msg.id);
        if (!alreadyExists) {
          state.messages[convId].data.push(msg);
        }
      }

      // Update last_message_preview and time in conversation list
      const conv = state.conversations.find((c) => c.id === convId);
      if (conv) {
        conv.last_message_preview = msg.content || '';
        conv.last_message_at = msg.created_at;
        if (msg.sender_type === 'CUSTOMER' && convId !== state.selectedId) {
          conv.agent_unread_count = (conv.agent_unread_count || 0) + 1;
        }
      }

      state.unreadTotal = calcUnreadTotal(state.conversations);
    },

    // WS: typing indicator from customer
    receiveTypingIndicator(state, action) {
      const { conversation_id, is_typing, sender_type } = action.payload;
      if (sender_type === 'CUSTOMER') {
        state.typingMap[conversation_id] = is_typing;
      }
    },

    // WS: messages_read event — customer read agent's messages
    receiveMessagesRead(state, action) {
      const { conversation_id } = action.payload;
      if (state.messages[conversation_id]) {
        state.messages[conversation_id].data.forEach((m) => {
          if (m.sender_type === 'AGENT') {
            m.is_read = true;
          }
        });
      }
    },

    // WS: conversation_update (status change etc.)
    receiveConversationUpdate(state, action) {
      const updated = action.payload;
      if (!updated || !updated.id) return;
      const idx = state.conversations.findIndex((c) => c.id === updated.id);
      if (idx !== -1) {
        state.conversations[idx] = { ...state.conversations[idx], ...updated };
      }
      if (state.detail && state.detail.id === updated.id) {
        state.detail = { ...state.detail, ...updated };
      }
      state.unreadTotal = calcUnreadTotal(state.conversations);
    },

    // WS: join_conversation response with last 30 messages
    receiveJoinMessages(state, action) {
      const { conversation_id, messages } = action.payload;
      state.messages[conversation_id] = {
        data: messages,
        page: 1,
        hasMore: messages.length >= 30,
      };
      // Clear unread count for selected conversation
      const conv = state.conversations.find((c) => c.id === conversation_id);
      if (conv) {
        conv.agent_unread_count = 0;
      }
      state.unreadTotal = calcUnreadTotal(state.conversations);
    },

    // Clear messages read count when agent opens a conversation
    clearUnreadForConversation(state, action) {
      const convId = action.payload;
      const conv = state.conversations.find((c) => c.id === convId);
      if (conv) {
        conv.agent_unread_count = 0;
      }
      state.unreadTotal = calcUnreadTotal(state.conversations);
    },

    clearError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // fetchConversations
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading.conversations = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading.conversations = false;
        const { data, count } = action.payload;
        const page = action.meta.arg?.page || 1;

        if (page === 1) {
          state.conversations = data;
        } else {
          // Append for pagination
          const existingIds = new Set(state.conversations.map((c) => c.id));
          const newOnes = data.filter((c) => !existingIds.has(c.id));
          state.conversations.push(...newOnes);
        }

        state.conversationsTotal = count;
        state.conversationsPage = page;
        state.conversationsHasMore = state.conversations.length < count;
        state.unreadTotal = calcUnreadTotal(state.conversations);
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading.conversations = false;
        state.error = action.payload;
      });

    // fetchConversationDetail
    builder
      .addCase(fetchConversationDetail.pending, (state) => {
        state.loading.detail = true;
      })
      .addCase(fetchConversationDetail.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.detail = action.payload.data;
      })
      .addCase(fetchConversationDetail.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload;
      });

    // fetchMessages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loading.messages = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading.messages = false;
        const { conversationId, page, data, count } = action.payload;

        if (page === 1) {
          state.messages[conversationId] = {
            data: data,
            page: 1,
            hasMore: data.length < count,
          };
        } else {
          // Prepend older messages
          const existing = state.messages[conversationId] || { data: [], page: 1, hasMore: true };
          const existingIds = new Set(existing.data.map((m) => m.id));
          const older = data.filter((m) => !existingIds.has(m.id));
          state.messages[conversationId] = {
            data: [...older, ...existing.data],
            page,
            hasMore: older.length > 0,
          };
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading.messages = false;
        state.error = action.payload;
      });

    // fetchStats
    builder
      .addCase(fetchStats.pending, (state) => {
        state.loading.stats = true;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.stats = action.payload.data;
      })
      .addCase(fetchStats.rejected, (state) => {
        state.loading.stats = false;
      });

    // sendMessage
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading.sending = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading.sending = false;
        const msg = action.payload.data;
        const convId = action.meta.arg.conversation_id;

        if (state.messages[convId]) {
          const exists = state.messages[convId].data.find((m) => m.id === msg.id);
          if (!exists) {
            state.messages[convId].data.push(msg);
          }
        }

        // Update preview in list
        const conv = state.conversations.find((c) => c.id === convId);
        if (conv) {
          conv.last_message_preview = msg.content || '';
          conv.last_message_at = msg.created_at;
          // First agent reply transitions PENDING → ACTIVE
          if (conv.status === 'PENDING') {
            conv.status = 'ACTIVE';
          }
          // Move to top
          const idx = state.conversations.indexOf(conv);
          if (idx > 0) {
            state.conversations.splice(idx, 1);
            state.conversations.unshift(conv);
          }
        }
        // Also update the detail panel status if this conversation is open
        if (state.detail && state.detail.id === convId && state.detail.status === 'PENDING') {
          state.detail.status = 'ACTIVE';
        }
      })
      .addCase(sendMessage.rejected, (state) => {
        state.loading.sending = false;
      });

    // resolveConversation
    builder
      .addCase(resolveConversation.pending, (state) => {
        state.loading.resolving = true;
      })
      .addCase(resolveConversation.fulfilled, (state, action) => {
        state.loading.resolving = false;
        const { id, status } = action.payload;
        const conv = state.conversations.find((c) => c.id === id);
        if (conv) conv.status = status;
        if (state.detail && state.detail.id === id) {
          state.detail.status = status;
        }
      })
      .addCase(resolveConversation.rejected, (state) => {
        state.loading.resolving = false;
      });
  },
});

export const {
  setSelectedId,
  setFilters,
  wsConnected,
  wsDisconnected,
  receiveNewConversation,
  receiveNewMessage,
  receiveTypingIndicator,
  receiveMessagesRead,
  receiveConversationUpdate,
  receiveJoinMessages,
  clearUnreadForConversation,
  clearError,
} = supportSlice.actions;

export default supportSlice.reducer;
